import { useId } from "react";
import { Search as SearchIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { WhatsAppTemplateDto } from "@multi-chat/shared";
import { cn } from "../../../lib/utils.js";
import { INBOX_SCROLLER_CLASS } from "../inbox-surface-classes.js";
import {
  templateLanguageDisplayName,
  templatePickerFilterOptions,
  templatePickerSnippet,
} from "../template-picker.js";
import { extractBodyPreview } from "../template-send.js";
import { TemplateMetaBadges, templateCategoryLabel } from "./TemplatePickerBadges.js";

export function TemplatePickerList({
  items,
  filteredItems,
  selectedId,
  query,
  languageFilter,
  categoryFilter,
  sending,
  onQueryChange,
  onLanguageFilterChange,
  onCategoryFilterChange,
  onSelect,
}: {
  items: readonly WhatsAppTemplateDto[];
  filteredItems: readonly WhatsAppTemplateDto[];
  selectedId: number | null;
  query: string;
  languageFilter: string | null;
  categoryFilter: string | null;
  sending: boolean;
  onQueryChange: (query: string) => void;
  onLanguageFilterChange: (language: string | null) => void;
  onCategoryFilterChange: (category: string | null) => void;
  onSelect: (templateId: number) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const searchId = useId();
  const { languages, categories } = templatePickerFilterOptions(items);
  const showLanguageFilter = languages.length > 1;
  const showCategoryFilter = categories.length > 1;

  return (
    <section
      className="flex min-h-0 min-w-0 flex-1 flex-col bg-inbox-thread"
      aria-label={t("inbox.templateSend.selectTemplate")}
    >
      <div className="flex shrink-0 flex-col gap-1.5 px-2 pt-2 pb-1.5">
        <label
          htmlFor={searchId}
          className="flex h-8 items-center gap-2 rounded-lg border border-inbox-line bg-surface px-2.5 transition-[border-color,box-shadow] duration-150 focus-within:border-primary focus-within:shadow-[0_0_0_3px_rgb(23_126_103/0.12)]"
        >
          <SearchIcon size={14} className="shrink-0 text-inbox-subtle" aria-hidden="true" />
          <input
            id={searchId}
            type="search"
            className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[0.78125rem] text-inbox-strong outline-none placeholder:text-inbox-subtle [unicode-bidi:plaintext]"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={t("inbox.templateSend.searchPlaceholder")}
            aria-label={t("inbox.templateSend.search")}
            disabled={sending}
            autoComplete="off"
          />
        </label>
        {showLanguageFilter || showCategoryFilter ? (
          <div className="flex flex-col gap-1">
            {showCategoryFilter ? (
              <FilterChipRow
                legend={t("inbox.templateSend.category")}
                allLabel={t("inbox.templateSend.allCategories")}
                value={categoryFilter}
                options={categories.map((code) => ({
                  value: code,
                  label: templateCategoryLabel(t, code),
                }))}
                disabled={sending}
                onChange={onCategoryFilterChange}
              />
            ) : null}
            {showLanguageFilter ? (
              <FilterChipRow
                legend={t("inbox.templateSend.language")}
                allLabel={t("inbox.templateSend.allLanguages")}
                value={languageFilter}
                options={languages.map((code) => ({
                  value: code,
                  label: templateLanguageDisplayName(code, i18n.language),
                }))}
                disabled={sending}
                onChange={onLanguageFilterChange}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      {filteredItems.length === 0 ? (
        <p className="m-0 px-3 py-6 text-center text-[0.75rem] text-inbox-muted">
          {t("inbox.templateSend.searchEmpty")}
        </p>
      ) : (
        <ul
          className={cn(
            "m-0 min-h-0 flex-1 list-none overflow-y-auto px-1.5 pt-0.5 pb-2",
            INBOX_SCROLLER_CLASS,
          )}
        >
          {filteredItems.map((item) => {
            const selected = item.id === selectedId;
            const preview = templatePickerSnippet(extractBodyPreview(item.components));
            return (
              <li key={item.id} className="py-px">
                <button
                  type="button"
                  aria-pressed={selected}
                  aria-current={selected ? "true" : undefined}
                  disabled={sending}
                  onClick={() => onSelect(item.id)}
                  className={cn(
                    "flex w-full cursor-pointer flex-col items-stretch gap-0.5 rounded-[0.5625rem] border px-2.5 py-[0.4375rem] text-start text-inherit transition-[background-color,border-color,box-shadow] duration-150",
                    selected
                      ? "border-primary/30 bg-surface shadow-inbox-float"
                      : "border-transparent bg-transparent hover:bg-surface",
                    "disabled:cursor-not-allowed disabled:opacity-65",
                  )}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    <bdi
                      title={item.name}
                      className="min-w-0 flex-1 truncate text-[0.78125rem] font-semibold text-inbox-strong"
                    >
                      {item.name}
                    </bdi>
                    <TemplateMetaBadges
                      category={item.category}
                      language={item.language}
                      compactLanguage
                    />
                  </span>
                  {preview ? (
                    <span
                      dir="auto"
                      className="truncate text-start text-[0.71875rem] leading-snug text-inbox-muted"
                    >
                      {preview}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function FilterChipRow({
  legend,
  allLabel,
  value,
  options,
  disabled,
  onChange,
}: {
  legend: string;
  allLabel: string;
  value: string | null;
  options: readonly { value: string; label: string }[];
  disabled: boolean;
  onChange: (value: string | null) => void;
}): React.JSX.Element {
  return (
    <div
      className="-mx-2 flex items-center gap-1 overflow-x-auto px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      role="group"
      aria-label={legend}
    >
      <FilterChip
        label={allLabel}
        pressed={value === null}
        disabled={disabled}
        onClick={() => onChange(null)}
      />
      {options.map((option) => (
        <FilterChip
          key={option.value}
          label={option.label}
          pressed={value === option.value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        />
      ))}
    </div>
  );
}

function FilterChip({
  label,
  pressed,
  disabled,
  onClick,
}: {
  label: string;
  pressed: boolean;
  disabled: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-full border px-2 text-[0.6875rem] transition-[background-color,border-color,color] duration-150",
        pressed
          ? "border-[#9fd6c3] bg-brand-mint font-semibold text-[#126c56]"
          : "border-[#dbe5e2] bg-surface text-[#30443f] hover:border-[#b9d4cb]",
      )}
    >
      {label}
    </button>
  );
}
